import React, { useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import styled from 'styled-components';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, Crosshair } from 'lucide-react';

// Fix for default marker icons in react-leaflet (same approach as MiniMap)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const MapWrapper = styled.div`
  width: 100%;
  height: ${props => props.$height || '260px'};
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  border: 1.5px solid #e5e7eb;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);

  .leaflet-container {
    height: 100%;
    width: 100%;
    z-index: 1;
    cursor: crosshair;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Coords = styled.span`
  font-size: 13px;
  color: ${props => props.theme?.colors?.muted || '#6b7280'};
  font-variant-numeric: tabular-nums;
`;

const GpsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme?.colors?.border || '#d1d5db'};
  background: ${props => props.theme?.colors?.surface || '#fff'};
  color: ${props => props.theme?.colors?.text || '#111827'};
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.backgroundSecondary || '#f3f4f6'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// Click-to-place + recenter helper. Lives inside MapContainer so it can use the map instance.
const MapInteractions = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

/**
 * Interactive location editor: a draggable Leaflet marker over OpenStreetMap tiles.
 * Users can drag the pin, click the map to move it, or use their device GPS.
 *
 * @param {number} latitude  current latitude (may be null before a pin is placed)
 * @param {number} longitude current longitude
 * @param {(lat:number, lng:number, accuracy?:number) => void} onChange called whenever the pin moves
 * @param {object} theme optional styled-components theme (for control colors)
 * @param {string} height CSS height for the map (default 260px)
 */
export const LocationEditorMap = ({ latitude, longitude, onChange, theme, height = '260px' }) => {
  const markerRef = useRef(null);
  const [locating, setLocating] = useState(false);

  // Fall back to a neutral default center when no pin is set yet, so the map still renders.
  const hasPin = latitude != null && longitude != null;
  const center = useMemo(
    () => (hasPin ? [latitude, longitude] : [43.6532, -79.3832]), // Toronto fallback
    [hasPin, latitude, longitude]
  );

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const { lat, lng } = marker.getLatLng();
          onChange(lat, lng);
        }
      },
    }),
    [onChange]
  );

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div>
      <MapWrapper $height={height}>
        <MapContainer center={center} zoom={hasPin ? 15 : 11} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapInteractions onPick={(lat, lng) => onChange(lat, lng)} />
          {hasPin && (
            <Marker
              position={[latitude, longitude]}
              draggable
              eventHandlers={eventHandlers}
              ref={markerRef}
            />
          )}
        </MapContainer>
      </MapWrapper>
      <Controls>
        <GpsButton type="button" onClick={useMyLocation} disabled={locating} theme={theme}>
          <Navigation size={14} />
          {locating ? 'Locating…' : 'Use my location'}
        </GpsButton>
        {hasPin ? (
          <Coords theme={theme}>
            <Crosshair size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {Number(latitude).toFixed(6)}, {Number(longitude).toFixed(6)}
          </Coords>
        ) : (
          <Coords theme={theme}>Click the map or use your location to set a pin</Coords>
        )}
      </Controls>
    </div>
  );
};

export default LocationEditorMap;
